import React from 'react';
import { Flex } from 'antd';
import ProctoringWatcher from './ProctoringWatcher';
import RetryEvaluationModal from './modals/RetryEvaluationModal';
import ExitFullScreenConfirmation from './modals/ExitFullScreenConfirmation';
import StartInterviewModal from './modals/StartInterviewModal';
import WarningMessage from './WarningMessage';
import ProgressSegment from './progressSegment/ProgressSegment';
import InterviewRoundContent from './interviewRoundContent/InterviewRoundContent';
import QuestionActionButtons from './interviewRoundContent/QuestionActionButtons';
import Draggable from 'react-draggable';
import CamRecorder from '../../../Components/CamRecorder/CamRecorder';
import AllowCameraConfirmation from './modals/AllowCameraConfirmation';
import { useInterviewRoundContext } from '../../../Context/InterviewRoundContext';
import useInterviewRoundsHandlers from '../hooks/useInterviewRoundsHandlers';
import useProctoring from '../hooks/useProctoring';

interface InterviewRoundPageProps {
  isSameDevice: boolean;
  terminatedStats: { isLimitReached: boolean; violationType: string };
}

const InterviewRoundPage: React.FC<InterviewRoundPageProps> = ({ isSameDevice, terminatedStats }) => {
  // Context and hooks
  const {
    currentSession,
    currentSessionQuestions,
    currentSessionQuestionAnswers,
    isRoundFinished,
    showExitConfirmation,
    setShowExitConfirmation,
    exitTimer,
    isFullscreen,
    showRetryEvaluation,
    activeQuestionIndex,
    showCamRecorder,
    showCameraConfirmation,
    showStartInterviewModal,
    elementRef,
  } = useInterviewRoundContext();

  const {
    handleEvaluation,
    handleCameraAccessConfirmation,
    handleCameraAccessdenied,
    handleCancelCameraAccessConfirmation,
    handleStartInterviewConfirmation,
    handleCancelStartInterview,
    handleRetryEvaluation,
    handleCancelExit,
    handleConfirmExit,
    handleAnswerSingleQuestion,
    handleBack,
    handleNext,
    handleQuestionAnswerChange,
    handleStartInterview,
    onNavigateToQuestions,
  } = useInterviewRoundsHandlers();

  const { handleFullScreenExitAttempt } = useProctoring();

  const handleConfirmAndFullscreenExit = async () => {
    if (currentSession) {
      await handleFullScreenExitAttempt(currentSession);
    }
    handleConfirmExit();
  };

  return (
    <section className="interview_round_main_container">
      <ProctoringWatcher />
      <div
        className="interview_round_fullscreen_container"
        ref={elementRef}
        tabIndex={0}
        style={{overflowY: 'auto'}}
      >
        {/* RETRY EVALUATION MODAL */}
        <RetryEvaluationModal
          containerRef={elementRef}
          showEvaluation={showRetryEvaluation}
          handleRetryEvaluation={handleRetryEvaluation}
        />

        {/* EXIT FULLSCREEN CONFIRMATION MODAL */}
        <ExitFullScreenConfirmation
          handleConfirmExit={handleConfirmAndFullscreenExit}
          containerRef={elementRef}
          isFullscreen={isFullscreen}
          showExitConfirmation={showExitConfirmation}
          setShowExitConfirmation={setShowExitConfirmation}
          handleCancelExit={handleCancelExit}
          exitTimer={exitTimer}
        />

        {/* START INTERVIEW MODAL */}
        <StartInterviewModal
          isOpen={showStartInterviewModal}
          isLoading={false}
          loadingMessage={null}
          onConfirm={handleStartInterviewConfirmation}
          onCancel={handleCancelStartInterview}
          containerRef={elementRef}
        />

        {/* Displaying alert message only if the round is not started or finished */}
        {activeQuestionIndex >= 0 && (
          <WarningMessage isRoundFinished={isRoundFinished} />
        )}

        {/* PROGRESS BAR */}
        {activeQuestionIndex >= 0 && (
          <Flex align="center" justify="center" style={{ width: "100%" }}>
            <ProgressSegment
              totalQuestions={currentSessionQuestions.length}
              currentQuestion={activeQuestionIndex}
              onQuestionClick={handleAnswerSingleQuestion}
              isRoundStarted={activeQuestionIndex > -1}
              isRoundFinished={isRoundFinished}
            />
          </Flex>
        )}

        {/* INTERVIEW ROUND DYNAMIC CONTENT */}
        {currentSession && (
          <InterviewRoundContent
            sessionQuestions={currentSessionQuestions}
            activeQuestionIndex={activeQuestionIndex}
            currentSession={currentSession}
            currentSessionQuestions={currentSessionQuestions}
            handleStartInterview={handleStartInterview}
            handleBack={handleBack}
            handleNext={handleNext}
            isRoundFinished={isRoundFinished}
            handleEvaluation={handleEvaluation}
            currentSessionQuestionAnswers={currentSessionQuestionAnswers}
            onNavigateToQuestions={onNavigateToQuestions}
            handleQuestionAnswerChange={handleQuestionAnswerChange}
            containerRef={elementRef}
            isSameDevice={isSameDevice}
            terminatedStats={terminatedStats}
          />
        )}

        {/* RENDERING QUESTIONS ACTION BUTTONS */}
        {activeQuestionIndex >= 0 &&
          activeQuestionIndex < currentSessionQuestions.length && (
            <QuestionActionButtons
              activeQuestionIndex={activeQuestionIndex}
              currentSessionQuestionAnswers={currentSessionQuestionAnswers}
              currentSessionQuestions={currentSessionQuestions}
              handleBack={handleBack}
              handleNext={handleNext}
              isRoundFinished={isRoundFinished}
            />
          )}

        {showCamRecorder && (
          <Draggable>
            <div
              className="video-recorder-container"
              style={{
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                zIndex: 9999,
                cursor: 'grab',
              }}
            >
              <CamRecorder handleCameraAccessdenied={handleCameraAccessdenied} />
            </div>
          </Draggable>
        )}
      </div>

      {/* CAMERA CONFIRMATION MODAL */}
      <AllowCameraConfirmation
        containerRef={elementRef}
        isOpen={showCameraConfirmation}
        handleCancelExit={handleCancelCameraAccessConfirmation}
        handleOk={handleCameraAccessConfirmation}
      />
    </section>
  );
};

export default InterviewRoundPage;